import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { BREED_LABELS } from "@/components/adtemplate/ColourField";
import { normGender } from "@/components/adtemplate/GenderSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

// [horse_info key, label, scan field]
const ROWS = [
  ["registered_name", "Horse Registered Name", "registered_name"], ["registration_number", "Registration Number", "registration_number"],
  ["date_of_birth", "Date of Birth", "date_of_birth"], ["year_born", "Year Born", "year_born"], ["horse_color", "Colour", "colour"],
  ["gender", "Gender", "gender"], ["sire", "Sire", "sire"], ["dam", "Dam", "dam"], ["breed", "Breed / Registry", "breed_registry"],
];
const found = (scan, k, f) => {
  const v = scan.fields?.[f] || "";
  if (k === "gender") return normGender(v);
  if (k === "breed") return v ? BREED_LABELS.find((b) => b.toUpperCase().includes(`(${v.toUpperCase()})`)) || v : "";
  return v;
};
const same = (a, b) => String(a || "").trim() === String(b || "").trim();

export function RegistrationReview({ scan, info, onApply, onClose }) {
  const [vals, setVals] = useState(() => Object.fromEntries(ROWS.map(([k, , f]) => [k, found(scan, k, f)])));
  const [use, setUse] = useState(() => Object.fromEntries(ROWS.map(([k, , f]) => { const v = found(scan, k, f); return [k, !!v && !same(v, info?.[k])]; })));
  const apply = () => {
    const patch = Object.fromEntries(ROWS.filter(([k]) => use[k] && String(vals[k]).trim()).map(([k]) => [k, String(vals[k]).trim()]));
    if (patch.year_born) patch.year_born_auto = false;
    onApply({ ...patch, registered: "Yes" });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="registration-review-dialog" className="w-[calc(100%-1rem)] max-w-3xl max-h-[92vh] overflow-y-auto rounded-xl p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>Information Found on Registration Papers</DialogTitle>
          <DialogDescription>{scan.registry ? `Looks like ${scan.registry} registration papers. ` : ""}Nothing changes until you confirm. Tick the values to use and correct anything that was read wrong. Blank means it couldn't be read confidently.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {ROWS.map(([k, label]) => {
            const cur = info?.[k] || "";
            const diff = !!cur && !!vals[k] && !same(cur, vals[k]);
            const unsure = (scan.uncertain || []).includes(ROWS.find((r) => r[0] === k)[2]) || !vals[k];
            return (
              <div key={k} data-testid={`reg-review-row-${k}`} className={`rounded-lg border p-3 grid grid-cols-1 sm:grid-cols-[150px_1fr_1fr_auto] gap-2 sm:items-center ${diff ? "border-amber-400 bg-amber-50" : "border-stone-200"}`}>
                <span className="text-sm font-semibold text-stone-800">{label}</span>
                <span className="text-sm text-stone-600"><span className="text-[11px] uppercase text-stone-400 mr-1">Entered:</span><span data-testid={`reg-review-current-${k}`}>{cur || "—"}</span></span>
                <div>
                  <span className="text-[11px] uppercase text-stone-400">Registration Papers{diff && <span className="ml-1 text-amber-800 font-semibold normal-case inline-flex items-center gap-0.5"><AlertTriangle className="h-3 w-3" />differs</span>}{unsure && <span className="ml-1 text-stone-500 normal-case">— please check</span>}</span>
                  <Input data-testid={`reg-review-found-${k}`} value={vals[k]} onChange={(e) => { setVals({ ...vals, [k]: e.target.value }); setUse({ ...use, [k]: true }); }} className="h-10 mt-0.5" />
                </div>
                <label className="flex items-center gap-2 text-xs font-semibold text-stone-700 min-h-[44px] cursor-pointer">
                  <Checkbox data-testid={`reg-review-use-${k}`} checked={!!use[k]} onCheckedChange={(v) => setUse({ ...use, [k]: !!v })} />Use
                </label>
              </div>
            );
          })}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" data-testid="reg-review-cancel" onClick={onClose} className="h-11 rounded-full">Not now</Button>
          <Button data-testid="reg-review-apply" onClick={apply} className="h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white">Confirm &amp; Apply Horse Information</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
